import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';

// Components
import { IsExportType, StyledWhiteBox } from 'src/features/PortDetail/config';
import BarChart from 'src/components/HighChart/Barchart';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

// Utilities
import { ITradeByPartner } from 'src/utils/typings/response';
import { useQueryDataTradeByPartnerChart } from 'src/services/queries';
import dayjs from 'dayjs';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';

interface IProps {
  locode: string;
  isExport: number;
}

const TradeByPartnerChart = ({ locode = '', isExport }: IProps) => {
  const theme = useTheme();
  const { data, isFetching } = useQueryDataTradeByPartnerChart({ locode, isExport });
  const [yearFilter, setYearFilter] = useState(dayjs().format(FORMAT_DATE_TIME.FULL_YEAR));
  const dataTradeByPartnerChartFull = getArrayDefault(data?.data);

  const listYears = useMemo(() => {
    const listYearsOnData: Array<string> = [];
    dataTradeByPartnerChartFull.forEach((tradePartner) => {
      const year = dayjs(tradePartner?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_YEAR);
      if (!listYearsOnData.includes(year)) listYearsOnData.push(year);
    });
    return listYearsOnData;
  }, [dataTradeByPartnerChartFull]);

  useEffect(() => {
    if (!isEmpty(listYears)) setYearFilter(listYears[0]);
  }, [listYears]);

  const dataTradeByPartnerChartFilter = useMemo(() => {
    return dataTradeByPartnerChartFull.filter(
      (tradePartner) =>
        dayjs(tradePartner?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_YEAR) === yearFilter,
    );
  }, [yearFilter]);

  const lineChartYData = useMemo(() => {
    const chinaData: Array<number> = [];
    const eastSouthAmericaData: Array<number> = [];
    const northWestEuropeData: Array<number> = [];
    const southEastAsiaData: Array<number> = [];
    const othersData: Array<number> = [];
    dataTradeByPartnerChartFilter.map((item: ITradeByPartner) => {
      chinaData.push(item.china || 0);
      eastSouthAmericaData.push(item.east_south_america || 0);
      northWestEuropeData.push(item.north_west_europe || 0);
      southEastAsiaData.push(item.south_east_asia || 0);
      othersData.push(item.others || 0);
    });
    return { chinaData, eastSouthAmericaData, northWestEuropeData, southEastAsiaData, othersData };
  }, [dataTradeByPartnerChartFilter]);

  const lineChartXData = useMemo(() => {
    const res = dataTradeByPartnerChartFilter.map((item: ITradeByPartner) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.SHORT_MONTH);
    });
    return res;
  }, [dataTradeByPartnerChartFilter]);

  return (
    <StyledWhiteBox flex={1}>
      <Stack mb={3} spacing={3} direction={'row'} alignItems={'center'} justifyContent="space-between">
        <Typography variant="h5">
          Monthly {isExport === IsExportType.Import ? 'imports' : 'exports'} by partner (thousand TEU)
        </Typography>
        <TextField
          value={yearFilter}
          onChange={(event) => {
            event.preventDefault();
            setYearFilter(event.target.value);
          }}
          id="non_profit_status"
          name="non_profit_status"
          size="small"
          placeholder="Enter"
          select
          label=""
        >
          {listYears.map((year) => (
            <MenuItem value={year} key={`trade-partner-${isExport}-${year}`}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <BarChart
        loading={isFetching}
        tooltipFormatter={function () {
          return `<b style="color: ${theme.palette.text.secondary}"></b><b>${this.point.category}</b>
<br/><b style="color: ${theme.palette.text.secondary}">Sea Shipments </b><b>${this.y}</b>`;
        }}
        yAxis={{
          min: 0,
          stackLabels: {
            enabled: false,
          },
        }}
        legend={{
          align: 'center',
          verticalAlign: 'bottom',
          backgroundColor: 'white',
          borderColor: 'transparent',
          borderWidth: 1,
          shadow: false,
        }}
        plotOptions={{
          column: {
            stacking: 'normal',
          },
        }}
        series={[
          {
            name: 'China',
            type: 'column',
            data: lineChartYData.chinaData,
            color: '#4338CA',
            borderWidth: 0,
          },
          {
            name: 'East South America',
            type: 'column',
            data: lineChartYData.eastSouthAmericaData,
            color: '#6366F1',
            borderWidth: 0,
          },
          {
            name: 'North West Europe',
            type: 'column',
            data: lineChartYData.northWestEuropeData,
            color: '#A5B4FC',
            borderWidth: 0,
          },
          {
            name: 'South East Asia',
            type: 'column',
            data: lineChartYData.southEastAsiaData,
            color: '#C7D2FE',
            borderWidth: 0,
          },
          {
            name: 'Others',
            type: 'column',
            data: lineChartYData.othersData,
            color: '#DDE2FA',
            borderWidth: 0,
          },
        ]}
        categories={lineChartXData}
        height={500}
      />
    </StyledWhiteBox>
  );
};

export default TradeByPartnerChart;
